import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Link } from 'react-router-dom';
import EditLinkModal from './EditLinkModal';
import ViewLinkModal from './ViewLinkModal';
import AddLinkModal from './AddLinkModal';
import ConfirmModal from '../../../components/ConfirmModal';

const Links = () => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken, user } = useAuth();
  
  // Состояние для данных
  const [links, setLinks] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLinks, setTotalLinks] = useState(0);
  const [limit] = useState(50); // 50 ссылок на страницу
  
  // Состояние для фильтров
  const [filters, setFilters] = useState({
    durl: '', // URL донора
    aurl: '', // URL акцептора
    lang: '', // язык ссылки
    dat_from: '', // дата создания с
    dat_to: '', // дата создания по
    td: '', // теги
    status: '', // статус
    ucr: '', // UCR
    text: '', // текст ссылки
  });
  
  // Состояние для сортировки
  const [sortField, setSortField] = useState('dat');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Состояние для модального окна редактирования
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  
  // Состояние для модального окна просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedViewLinkId, setSelectedViewLinkId] = useState(null);
  
  // Состояние для модального окна добавления
  const [addModalOpen, setAddModalOpen] = useState(false);
  
  // Состояние для индикаторов загрузки
  const [checkingLinks, setCheckingLinks] = useState(new Set());
  const [deletingLinks, setDeletingLinks] = useState(new Set());
  
  // Состояние для модалки удаления
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);
  
  // Состояние для раскрытия фильтров
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Загрузка данных
  const fetchLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }
      
      // Формируем параметры запроса
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
      });
      
      // Получаем ID пользователя из контекста авторизации
      const userId = user?.id;
      
      // Добавляем фильтры, если они заполнены
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // Для строковых значений используем trim, для числовых - преобразуем в строку
          const stringValue = typeof value === 'string' ? value.trim() : String(value);
          if (stringValue !== '') {
            // Маппинг параметров фильтров согласно API
            switch (key) {
              case 'ucr':
                if (stringValue === 'my' && userId) {
                  params.append('user_id', userId.toString());
                } else if (stringValue !== 'my') {
                  params.append('user_id', stringValue);
                }
                break;
              case 'lang':
                params.append('link_lang', stringValue);
                break;
              case 'td':
                params.append('tags', stringValue);
                break;
              case 'text':
                params.append('text', stringValue);
                break;
              case 'status':
                // Статус должен быть числом, но только если значение не пустое
                if (stringValue !== '') {
                  const statusValue = parseInt(stringValue);
                  if (!isNaN(statusValue)) {
                    params.append('status', statusValue.toString());
                  }
                }
                break;
              default:
                params.append(key, stringValue);
                break;
            }
          }
        }
      });
      
      const url = `${API_URL}${currentLang}/links?${params.toString()}`;
      
      const response = await fetchWithAuth(url, {
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setLinks(data.links || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalLinks(data.pagination?.total || 0);
      } else {
        throw new Error(data.message || 'Ошибка загрузки ссылок');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };


  // Генерация уникального цвета для сайта по его id
  const getSiteColor = (siteId) => {
    if (!siteId) return '#888';
    // Преобразуем id в число (если строка)
    const idNum = typeof siteId === 'number' ? siteId : parseInt(siteId, 10);
    // Простая формула для генерации hue
    const hue = (idNum * 57) % 360;
    return `hsl(${hue}, 70%, 45%)`;
  };
  const fetchSites = async () => {
    try {
      setLoading(true);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites`);

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        // Ошибка загрузки сайтов
      }
    } catch (error) {
      // Ошибка загрузки сайтов
    } finally {
      setLoading(false);
    }
  };
  
  // Загружаем данные при изменении параметров
  useEffect(() => {
    fetchLinks();
    fetchSites();
  }, [currentPage, sortField, sortDirection, filters]);
  
  // Автоматически раскрываем фильтры при наличии активных фильтров
  useEffect(() => {
    if (getActiveFiltersCount() > 0 && !filtersExpanded) {
      setFiltersExpanded(true);
    }
  }, [filters]);
  
  // Обработчики фильтров
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  };
  
  // Обработчик сортировки
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };
  
  // Подсчет активных фильтров
  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => {
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true; // Для числовых значений
    }).length;
  };

  // Функции для работы с датами
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      from: startOfMonth.toISOString().split('T')[0],
      to: endOfMonth.toISOString().split('T')[0]
    };
  };

  const getLastMonthRange = () => {
    const now = new Date();
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    
    return {
      from: startOfLastMonth.toISOString().split('T')[0],
      to: endOfLastMonth.toISOString().split('T')[0]
    };
  };

  // Очистка фильтров
  const clearFilters = () => {
    setFilters({
      durl: '',
      aurl: '',
      lang: '',
      dat_from: '',
      dat_to: '',
      td: '',
      status: '',
      ucr: '',
      text: '',
    });
    setCurrentPage(1);
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };
  
  // Обрезка длинного URL
  const truncateUrl = (url, maxLength = 50) => {
    if (!url) return '-';
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  // Преобразуем URL/домен донора в DNS (hostname) для подсчета уникальных доноров
  const getDnsFromUrl = (value) => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';

    try {
      let withProto = raw;
      if (raw.startsWith('//')) {
        withProto = `https:${raw}`;
      } else if (!(raw.startsWith('http://') || raw.startsWith('https://'))) {
        withProto = `https://${raw}`;
      }
      const hostname = new URL(withProto).hostname || '';
      return hostname.toLowerCase().replace(/^www\./, '');
    } catch (e) {
      // Fallback: пытаемся вытащить hostname грубо
      const noProto = raw.replace(/^https?:\/\//, '').replace(/^\/\//, '').split('/')[0];
      const hostOnly = noProto.split(':')[0] || '';
      return hostOnly.toLowerCase().replace(/^www\./, '');
    }
  };

  const uniqueDonorsDnsCount = React.useMemo(() => {
    const dnsSet = new Set();
    links.forEach((link) => {
      const dns = getDnsFromUrl(link?.durl);
      if (dns) dnsSet.add(dns);
    });
    return dnsSet.size;
  }, [links]);
  
  // Получение иконки сортировки
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Функции для действий с ссылками
  const handleViewLink = (linkId) => {
    setSelectedViewLinkId(linkId);
    setViewModalOpen(true);
  };

  const handleEditLink = (linkId) => {
    // Проверяем, что пользователь является владельцем ссылки
    const link = links.find(l => l.id === linkId);
    if (!link) {
      alert(currentLang === 'ru' ? 'Ссылка не найдена' : 'Link not found');
      return;
    }

    if (user?.id !== link.ucr) {
      alert(currentLang === 'ru' ? 'Вы можете редактировать только свои ссылки' : 'You can only edit your own links');
      return;
    }

    setSelectedLinkId(linkId);
    setEditModalOpen(true);
  };

  const handleCheckLink = async (linkId) => {
    setCheckingLinks(prev => new Set(prev).add(linkId));
    
    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/links/${linkId}/check`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем данные на странице
        fetchLinks();
        showNotification(currentLang === 'ru' ? 'Ссылка проверена!' : 'Link checked!', 'success');
      } else {
        throw new Error(data.message || 'Ошибка проверки ссылки');
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setCheckingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkId);
        return newSet;
      });
    }
  };

  // Показать модалку подтверждения удаления
  const showDeleteConfirm = (linkId) => {
    // Находим ссылку в списке для проверки владельца
    const link = links.find(l => l.id === linkId);
    if (!link) {
      showNotification(currentLang === 'ru' ? 'Ссылка не найдена' : 'Link not found', 'error');
      return;
    }

    // Проверяем, что пользователь является владельцем ссылки
    if (user?.id !== link.ucr) {
      showNotification(currentLang === 'ru' ? 'Вы можете удалять только свои ссылки' : 'You can only delete your own links', 'error');
      return;
    }

    setLinkToDelete(linkId);
    setShowDeleteModal(true);
  };

  // Подтверждение удаления ссылки
  const handleDeleteConfirm = async () => {
    if (!linkToDelete) return;

    setDeletingLinks(prev => new Set(prev).add(linkToDelete));

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/links/${linkToDelete}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем данные на странице
        fetchLinks();
        showNotification(currentLang === 'ru' ? 'Ссылка удалена!' : 'Link deleted!', 'success');
      } else {
        throw new Error(data.message || 'Ошибка удаления ссылки');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setDeletingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkToDelete);
        return newSet;
      });
      setLinkToDelete(null);
    }
  };

  // Закрыть модалку удаления
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setLinkToDelete(null);
  };

  const handleEditSuccess = () => {
    // Обновляем данные на странице после успешного редактирования
    fetchLinks();
    showNotification(currentLang === 'ru' ? 'Ссылка успешно обновлена!' : 'Link updated successfully!', 'info');
  };

  const handleAddSuccess = () => {
    // Обновляем данные на странице после успешного добавления
    fetchLinks();
    showNotification(currentLang === 'ru' ? 'Ссылки успешно созданы!' : 'Links created successfully!', 'success');
  };

  // Функция показа уведомлений
  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  // Функция копирования в буфер обмена
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(currentLang === 'ru' ? 'Ссылка скопирована!' : 'Link copied!', 'success');
    } catch (error) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        showNotification(currentLang === 'ru' ? 'Ссылка скопирована!' : 'Link copied!', 'success');
      } catch (fallbackError) {
        showNotification(currentLang === 'ru' ? 'Ошибка копирования' : 'Copy failed', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="p-2 sm:p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <div className="px-3 sm:px-4 py-2 bg-purple-500 text-white rounded-lg flex items-center space-x-2 text-sm sm:text-base">
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" className="h-5 w-5 sm:h-6 sm:w-6" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path fill="none" d="M0 0h24v24H0z"></path><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"></path></svg>
            <span>{currentLang === 'ru' ? 'SEO Ссылки' : 'SEO Links'}</span>
          </div>
          <Link
            to="/seo/donors"
            className="px-3 sm:px-4 py-2 border border-purple-500 text-purple-500 rounded-lg flex items-center space-x-2 text-sm sm:text-base"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="hidden sm:inline">{currentLang === 'ru' ? 'SEO Доноры' : 'SEO Donors'}</span>
            <span className="sm:hidden">{currentLang === 'ru' ? 'Доноры' : 'Donors'}</span>
          </Link>
          {getActiveFiltersCount() > 0 && (
            <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
              {getActiveFiltersCount()} {currentLang === 'ru' ? 'фильтр' : 'filter'}
              {getActiveFiltersCount() > 1 && (currentLang === 'ru' ? 'а' : 's')}
            </span>
          )}
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
          <button
            onClick={() => setAddModalOpen(true)}
            className="px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center space-x-2 text-sm sm:text-base"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{currentLang === 'ru' ? 'Добавить ссылки' : 'Add Links'}</span>
          </button>
          <button
            onClick={fetchLinks}
            disabled={loading}
            className="px-3 sm:px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 text-sm sm:text-base"
          >
            {loading ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') : (currentLang === 'ru' ? 'Обновить' : 'Refresh')}
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-lg shadow-md mb-4 sm:mb-6 dark:bg-navy-700 dark:text-white dark:border-white/10">
        {/* Заголовок фильтров */}
        <div 
          className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <h3 className="text-base sm:text-lg font-semibold">{currentLang === 'ru' ? 'Фильтры' : 'Filters'}</h3>
              {getActiveFiltersCount() > 0 && (
                <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
                  {getActiveFiltersCount()}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {getActiveFiltersCount() > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFilters();
                  }}
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                >
                  {currentLang === 'ru' ? 'Очистить' : 'Clear'}
                </button>
              )}
              <svg 
                className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {/* Быстрые фильтры */}
          <div className="mt-3 flex flex-wrap gap-1 sm:gap-2">
          <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('ucr', '');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.ucr === '' 
                  ? 'bg-brand-500 text-white border-brand-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Все ссылки' : 'All links'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Все' : 'All'}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('ucr', 'my');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.ucr === 'my' 
                  ? 'bg-brand-500 text-white border-brand-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Мои ссылки' : 'My links'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Мои' : 'My'}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '' 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Все' : 'All'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '1');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '1' 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Активные' : 'Active'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Акт' : 'Act'}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '0');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '0' 
                  ? 'bg-yellow-500 text-white border-yellow-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Ожидают проверки' : 'Pending'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Ожид' : 'Pend'}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '2');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '2' 
                  ? 'bg-red-500 text-white border-red-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Не найдены' : 'Not found'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Не найд' : 'Not f'}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '99');
              }}
              className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                filters.status === '99' 
                  ? 'bg-gray-300 text-gray-800' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{currentLang === 'ru' ? 'Сняты' : 'Removed'}</span>
              <span className="sm:hidden">{currentLang === 'ru' ? 'Сняты' : 'Rem'}</span>
            </button>
          </div>

          {/* Быстрые фильтры по сайтам */}
          {sites.length > 0 && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  {currentLang === 'ru' ? 'Быстрые фильтры по сайтам:' : 'Quick site filters:'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange('aurl', '');
                  }}
                  className={`px-2 sm:px-3 py-1 text-xs rounded-full border transition-colors ${
                    filters.aurl === '' 
                      ? 'bg-brand-500 text-white border-brand-500' 
                      : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {currentLang === 'ru' ? 'Все сайты' : 'All sites'}
                </button>
                {sites.map((site) => site.status == 1 && (
                  <button
                    key={site.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterChange('aurl', site.dns);
                    }}
                    className={`px-2 sm:px-3 py-1 text-xs rounded-full border transition-colors flex items-center space-x-1 ${
                      filters.aurl === site.dns
                        ? 'text-white border-current'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}
                    style={{
                      backgroundColor: filters.aurl === site.dns ? getSiteColor(site.id) : undefined,
                      borderColor: filters.aurl === site.dns ? getSiteColor(site.id) : undefined
                    }}
                  >
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: filters.aurl === site.dns ? 'white' : getSiteColor(site.id) }}
                    ></div>
                    <span className="max-w-[120px] sm:max-w-[150px] truncate">
                      {site.dns}
                    </span>
                    <span className={`text-xs ${filters.site_id === site.id ? 'text-white' : 'text-gray-500'}`}>
                      {site.status === 1 ? '' : '(Н)'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Фильтры по датам */}
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const currentMonth = getCurrentMonthRange();
                  handleFilterChange('dat_from', currentMonth.from);
                  handleFilterChange('dat_to', currentMonth.to);
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.dat_from === getCurrentMonthRange().from && filters.dat_to === getCurrentMonthRange().to
                    ? 'bg-purple-500 text-white border-purple-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Этот месяц' : 'This month'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const lastMonth = getLastMonthRange();
                  handleFilterChange('dat_from', lastMonth.from);
                  handleFilterChange('dat_to', lastMonth.to);
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.dat_from === getLastMonthRange().from && filters.dat_to === getLastMonthRange().to
                    ? 'bg-purple-500 text-white border-purple-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Прошлый месяц' : 'Last month'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange('dat_from', '');
                  handleFilterChange('dat_to', '');
                }}
                className={`px-2 sm:px-3 py-1 text-xs rounded-full border ${
                  filters.dat_from === '' && filters.dat_to === ''
                    ? 'bg-purple-500 text-white border-purple-500' 
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {currentLang === 'ru' ? 'Все даты' : 'All dates'}
              </button>
            </div>
          </div>
        </div>
        

        {/* Раскрывающиеся фильтры */}
        {filtersExpanded && (
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {/* URL донора */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'URL донора' : 'Donor URL'}
                </label>
                <input
                  type="text"
                  value={filters.durl}
                  onChange={(e) => handleFilterChange('durl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите URL донора' : 'Enter donor URL'}
                />
              </div>
              
              {/* URL акцептора */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'URL акцептора' : 'Acceptor URL'}
                </label>
                <input
                  type="text"
                  value={filters.aurl}
                  onChange={(e) => handleFilterChange('aurl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите URL акцептора' : 'Enter acceptor URL'}
                />
              </div>
              
              {/* Язык ссылки */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Язык ссылки' : 'Link language'}
                </label>
                <select
                  value={filters.lang}
                  onChange={(e) => handleFilterChange('lang', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                >
                  <option value="">{currentLang === 'ru' ? 'Все языки' : 'All languages'}</option>
                  <option value="ru">Русский</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              
              {/* Статус */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Статус' : 'Status'}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                >
                  <option value="">{currentLang === 'ru' ? 'Все статусы' : 'All statuses'}</option>
                  <option value="0">{currentLang === 'ru' ? 'Ожидает проверки' : 'Pending'}</option>
                  <option value="1">{currentLang === 'ru' ? 'Активная' : 'Active'}</option>
                  <option value="2">{currentLang === 'ru' ? 'Не найдена' : 'Not found'}</option>
                  <option value="99">{currentLang === 'ru' ? 'Снята' : 'Removed'}</option>
                </select>
              </div>
              
              {/* Пользователь */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Пользователь' : 'User'}
                </label>
                <select
                  value={filters.ucr}
                  onChange={(e) => handleFilterChange('ucr', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                >
                  <option value="">{currentLang === 'ru' ? 'Все ссылки' : 'All links'}</option>
                  <option value="my">{currentLang === 'ru' ? 'Мои ссылки' : 'My links'}</option>
                </select>
              </div>
              
              {/* Теги */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Теги' : 'Tags'}
                </label>
                <input
                  type="text"
                  value={filters.td}
                  onChange={(e) => handleFilterChange('td', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите теги' : 'Enter tags'}
                />
              </div>
              
              {/* Текст ссылки */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Текст ссылки' : 'Link text'}
                </label>
                <input
                  type="text"
                  value={filters.text}
                  onChange={(e) => handleFilterChange('text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите текст ссылки' : 'Enter link text'}
                />
              </div>
              
              {/* Дата создания с */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата создания с' : 'Created from'}
                </label>
                <input
                  type="date"
                  value={filters.dat_from}
                  onChange={(e) => handleFilterChange('dat_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
              
              {/* Дата создания по */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата создания по' : 'Created to'}
                </label>
                <input
                  type="date"
                  value={filters.dat_to}
                  onChange={(e) => handleFilterChange('dat_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
              

            </div>
          </div>
        )}
      </div>

      {/* Статистика */}
      <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6 dark:bg-navy-700 dark:text-white dark:border-white/10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div>
            <span className="text-xs sm:text-sm text-gray-600">
              {currentLang === 'ru' ? 'Всего ссылок:' : 'Total links:'} {totalLinks}
            </span>
            <div className="mt-1">
              <span className="text-xs sm:text-sm text-gray-600">
                {currentLang === 'ru' ? 'Уникальных доноров (DNS):' : 'Unique donors (DNS):'} {uniqueDonorsDnsCount}
              </span>
            </div>
          </div>
          <div>
            <span className="text-xs sm:text-sm text-gray-600">
              {currentLang === 'ru' ? 'Страница' : 'Page'} {currentPage} {currentLang === 'ru' ? 'из' : 'of'} {totalPages}
            </span>
          </div>
        </div>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Таблица */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden dark:bg-navy-700 dark:text-white dark:border-white/10">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-white/10">
                  <tr>
                    <th 
                      className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                    >
                     #
                    </th>
                    <th 
                       className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                     >
                       <span className="hidden sm:inline">{currentLang === 'ru' ? 'Статус' : 'Status'}</span>
                       <span className="sm:hidden">{currentLang === 'ru' ? 'Стат' : 'Stat'}</span>
                     </th>
                     <th 
                       className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                       onClick={() => handleSort('dat')}
                     >
                       {currentLang === 'ru' ? 'Дата' : 'Date'} {getSortIcon('dat')}
                     </th>
                    <th 
                      className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                      onClick={() => handleSort('durl')}
                    >
                      <span className="hidden sm:inline">{currentLang === 'ru' ? 'URL донора' : 'Donor URL'}</span>
                      <span className="sm:hidden">{currentLang === 'ru' ? 'Донор' : 'Donor'}</span> {getSortIcon('durl')}
                    </th>
                    <th 
                      className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                      onClick={() => handleSort('aurl')}
                    >
                      <span className="hidden sm:inline">{currentLang === 'ru' ? 'URL акцептора' : 'Acceptor URL'}</span>
                      <span className="sm:hidden">{currentLang === 'ru' ? 'Акцептор' : 'Acceptor'}</span> {getSortIcon('aurl')}
                    </th>
                     <th 
                       className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                       onClick={() => handleSort('td')}
                     >
                       {currentLang === 'ru' ? 'Теги' : 'Tags'} {getSortIcon('td')}
                     </th>
                     <th className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:bg-navy-700 dark:text-white dark:border-white/10">
                       <span className="hidden sm:inline">{currentLang === 'ru' ? 'SEO параметры' : 'SEO Parameters'}</span>
                       <span className="sm:hidden">{currentLang === 'ru' ? 'SEO' : 'SEO'}</span>
                     </th>
                     <th 
                       className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                       onClick={() => handleSort('ucr')}
                     >
                       <span className="hidden sm:inline">{currentLang === 'ru' ? 'Пользователь' : 'User'}</span>
                       <span className="sm:hidden">{currentLang === 'ru' ? 'Польз' : 'User'}</span> {getSortIcon('ucr')}
                     </th>
                     <th className="p-2 sm:p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:bg-navy-700 dark:text-white dark:border-white/10">
                       <span className="hidden sm:inline">{currentLang === 'ru' ? 'Действия' : 'Actions'}</span>
                       <span className="sm:hidden">{currentLang === 'ru' ? 'Дейст' : 'Actions'}</span>
                     </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10">
                  {links.map((link) => (
                    <tr key={link.id} className="hover:bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-white/10">
                      <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        <div className="flex md:flex-row flex-col md:space-x-2 items-center">
                          <button
                            onClick={() => handleViewLink(link.id)}
                            className="p-1 md:p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                            title={currentLang === 'ru' ? 'Просмотр' : 'View'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCheckLink(link.id)}
                            disabled={checkingLinks.has(link.id)}
                            className="p-1 md:p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md disabled:opacity-50"
                            title={currentLang === 'ru' ? 'Проверить' : 'Check'}
                          >
                            {checkingLinks.has(link.id) ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (() => {
                              // Проверяем, была ли проверка сегодня
                              const today = new Date().toISOString().split('T')[0];
                              const lastCheckDate = link.dend;
                              const isCheckedToday = lastCheckDate === today;
                              
                              if (isCheckedToday) {
                                // Если проверяли сегодня - показываем галочку
                                return (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                );
                              } else {
                                // Если не проверяли сегодня - показываем иконку обновления
                                return (
                                  <svg className="w-4 h-4" fill="none" stroke="gray" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                );
                              }
                            })()}
                          </button>
                          {user?.id === link.ucr && (
                            <button
                              onClick={() => handleEditLink(link.id)}
                              className="p-1 md:p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                              title={currentLang === 'ru' ? 'Редактировать' : 'Edit'}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                         <span className={`px-1 sm:px-2 py-1 text-xs font-bold rounded-full ${
                           link.status === 1 ? 'bg-green-100 text-green-800' :
                           link.status === 0 ? 'bg-yellow-100 text-yellow-800' :
                           link.status === 2 ? 'bg-red-100 text-red-800' :
                           link.status === 99 ? 'bg-gray-100 text-gray-800' :
                           'bg-gray-100 text-gray-800'
                         }`}>
                           <span>
                             {link.status === 0 ? (currentLang === 'ru' ? 'Ожидает проверки' : 'Pending') :
                              link.status === 1 ? (currentLang === 'ru' ? 'Активная' : 'Active') :
                              link.status === 2 ? (currentLang === 'ru' ? 'Не найдена' : 'Not found') :
                              link.status === 99 ? (currentLang === 'ru' ? 'Снята' : 'Removed') :
                              '-'}
                           </span>
                         </span>
                         <div className='mt-1 text-xs'>{formatDate(link.dend)}</div>
                       </td>
                       <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:bg-navy-700 dark:text-white dark:border-white/10">
                         <div>{formatDate(link.dat)}</div>
                       </td>
                      <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => copyToClipboard(link.durl)}
                            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                            title={currentLang === 'ru' ? 'Копировать ссылку' : 'Copy link'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          <div className="max-w-xs flex-1">
                            <a 
                              href={link.durl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 truncate block"
                              title={link.durl}
                            >
                              {truncateUrl(link.durl)}
                            </a>
                          </div>
                        </div>
                        {link.meta_title && <div><span className="px-2 py-0 text-xs font-medium bg-green-100 text-black rounded-md">{link.meta_title}</span></div>} 
                      </td>
                      <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <button
                            onClick={() => copyToClipboard(link.aurl)}
                            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                            title={currentLang === 'ru' ? 'Копировать ссылку' : 'Copy link'}
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          <div className="max-w-[150px] sm:max-w-xs flex-1">
                            <a 
                              href={link.aurl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 truncate block text-xs sm:text-sm"
                              title={link.aurl}
                            >
                              {truncateUrl(link.aurl, 30)}
                            </a>
                          </div>
                        </div>
                        {link.text && <div className="mt-1"><span className="px-1 sm:px-2 py-0 text-xs font-medium bg-green-100 text-black rounded-md">{link.text}</span></div>} 
                      </td>
                       <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                         {link.td ? (
                           <div className="flex flex-wrap gap-0.5 sm:gap-1">
                             {link.td.split(',').map((tag, index) => (
                               <span
                                 key={index}
                                 className="px-1 sm:px-2 py-0.5 sm:py-1 text-xs font-medium bg-gray-100 text-blue-800 rounded-full"
                               >
                                 {tag.trim()}
                               </span>
                             ))}
                           </div>
                         ) : (
                           '-'
                         )}
                       </td>
                       
                       
                       <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                         <div className="space-y-1">
                            <div className="flex items-center space-x-1">
                               <span className="text-xs text-gray-500">
                                 {currentLang === 'ru' ? 'Язык:' : 'Lang:'}
                               </span>
                               <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                                  link.lang === 'ru' ? 'bg-blue-100 text-blue-800' :
                                  link.lang === 'en' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {link.lang?.toUpperCase() || '-'}
                                </span>
                             </div>
                         
                           {/* Количество ссылок */}
                           {link.stat_links && link.stat_links > 0 ? (
                             <div className="flex items-center space-x-1">
                               <span className="text-xs text-gray-500">
                                 {currentLang === 'ru' ? 'Ссылки:' : 'Links:'}
                               </span>
                               <span className="px-1 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                                 {link.stat_links}
                               </span>
                             </div>
                           ) : ''}
                           
                           {/* Позиция */}
                           {link.stat_pos && link.stat_pos != 'unknown' && (
                             <div className="flex items-center space-x-1">
                               <span className="text-xs text-gray-500">
                                 {currentLang === 'ru' ? 'Позиция:' : 'Pos:'}
                               </span>
                               <span className="px-1 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                 {link.stat_pos}
                               </span>
                             </div>
                           )}
                           
                           {/* Атрибуты */}
                           {link.stat_attr && link.stat_attr != '' && (
                             <div className="flex items-start space-x-1">
                               <span className="text-xs text-gray-500 mt-0.5">
                                 {currentLang === 'ru' ? 'Атрибуты:' : 'Attr:'}
                               </span>
                               <div className="flex flex-wrap gap-0.5">
                                 {link.stat_attr.split(',').map((attr, index) => (
                                   <span
                                     key={index}
                                     className="px-1 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded"
                                   >
                                     {attr.trim()}
                                   </span>
                                 ))}
                               </div>
                             </div>
                           )}
                         </div>
                       </td>
                       <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                         <span className="px-1 sm:px-2 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-800">
                          <span className="hidden sm:inline">{link.user_name}</span>
                          <span className="sm:hidden">{link.user_name?.substring(0, 8)}...</span>
                         </span>
                       </td>
                       <td className="p-2 sm:p-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                         <div className="flex space-x-2">
                           {user?.id === link.ucr && (
                             <button
                               onClick={() => showDeleteConfirm(link.id)}
                               disabled={deletingLinks.has(link.id)}
                               className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md disabled:opacity-50"
                               title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
                             >
                               {deletingLinks.has(link.id) ? (
                                 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                               ) : (
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                 </svg>
                               )}
                             </button>
                           )}
                         </div>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="bg-white px-3 sm:px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    {currentLang === 'ru' ? 'Предыдущая' : 'Previous'}
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-2 sm:ml-3 relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    {currentLang === 'ru' ? 'Следующая' : 'Next'}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-700">
                      {currentLang === 'ru' ? 'Показано' : 'Showing'} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {currentLang === 'ru' ? 'до' : 'to'} <span className="font-medium">{Math.min(currentPage * limit, totalLinks)}</span> {currentLang === 'ru' ? 'из' : 'of'} <span className="font-medium">{totalLinks}</span> {currentLang === 'ru' ? 'результатов' : 'results'}
                    </p>
                  </div>
    <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        ←
                      </button>
                      
                      {/* Номера страниц */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-3 sm:px-4 py-2 border text-xs sm:text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Сообщение об отсутствии данных */}
      {!loading && links.length === 0 && !error && (
        <div className="text-center py-8 text-gray-500">
          {currentLang === 'ru' ? 'Ссылки не найдены' : 'Links not found'}
        </div>
      )}
      
      {/* Модальное окно редактирования ссылки */}
      <EditLinkModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedLinkId(null);
        }}
        linkId={selectedLinkId}
        onSuccess={handleEditSuccess}
      />
      
      {/* Модальное окно просмотра ссылки */}
      <ViewLinkModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedViewLinkId(null);
        }}
        linkId={selectedViewLinkId}
      />
      
      {/* Модальное окно добавления ссылки */}
      <AddLinkModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={currentLang === 'ru' 
          ? 'Вы уверены, что хотите удалить эту ссылку? Это действие нельзя отменить.' 
          : 'Are you sure you want to delete this link? This action cannot be undone.'
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />
    </div>
  );
};

export default Links;
